import { useCallback, useMemo } from 'react';

import { useRouter } from '#router/useRouter.js';
import { useAuthentication } from '@givebacks/api';
import {
  showConsumerPostCheckoutQuery,
  showConsumerPostCheckoutReceiptQuery,
} from '@givebacks/api/store';
import { useQuery } from '@tanstack/react-query';
import { ClickableOptionsCard } from 'core/Components';
import { Group, Modal, Space, Stack, Text, ThemeIcon } from 'ui';
import { IconCheck } from 'ui/Icons';

import { useSessionCheckoutForm } from './utils';

type CheckoutConfirmationModalProps = {
  opened: boolean;
  onClose: () => void;
  checkoutId?: string;
};

type CheckoutConfirmationModalContentProps = {
  checkoutId: string;
};

const CheckoutOrderNumbers = ({ checkoutId }: { checkoutId: string }) => {
  const { data: receiptData } = useQuery(
    showConsumerPostCheckoutReceiptQuery({ postCheckoutId: checkoutId })
  );

  return (
    <Stack>
      {receiptData?.carts.map((cart) => (
        <Group key={cart.uuid}>
          <ThemeIcon size={36} color="mint" radius="lg">
            <IconCheck />
          </ThemeIcon>
          <Stack gap="0">
            <Text c="gray.8">Order Number: {cart.uuid}</Text>
            <Text c="gray.6" fz="12px">
              {cart.cause.name}
            </Text>
          </Stack>
        </Group>
      ))}
    </Stack>
  );
};

const CheckoutConfirmationModalContent = ({
  checkoutId,
}: CheckoutConfirmationModalContentProps) => {
  const { useNavigate } = useRouter();
  const navigate = useNavigate();
  const { form } = useSessionCheckoutForm();

  const { data: checkoutData } = useQuery(
    showConsumerPostCheckoutQuery({ postCheckoutId: checkoutId })
  );

  const { isAuthenticated } = useAuthentication();

  const isMembership = checkoutData?.membership;
  const isTicket = checkoutData?.ticket;

  const encodedEmail = encodeURIComponent(form?.email ?? '');

  const cartParams = useMemo(() => {
    const params = new URLSearchParams();
    params.append('firstName', form?.firstName ?? '');
    params.append('lastName', form?.lastName ?? '');
    params.append('email', encodedEmail);
    params.append('checkout_id', checkoutId ?? '');
    return params.toString();
  }, [checkoutId, encodedEmail, form?.firstName, form?.lastName]);

  const handleMembership = useCallback(() => {
    if (isAuthenticated) {
      return navigate('/account/memberships');
    } else {
      return navigate(`/post-checkout/membership?${cartParams}`);
    }
  }, [cartParams, isAuthenticated, navigate]);

  const handlePurchase = useCallback(() => {
    if (isAuthenticated) {
      return navigate('/account/purchases');
    } else {
      navigate(`/post-checkout/receipt?${cartParams}`);
    }
  }, [cartParams, isAuthenticated, navigate]);

  // Can be used to handle ticket purchases. Links will need to be updated.
  const handleTicketPurchase = useCallback(() => {
    if (isAuthenticated) {
      return navigate('/account/events');
    } else {
      navigate(`/tickets?checkout_id=${checkoutId}&email=${encodedEmail}`);
    }
  }, [isAuthenticated, navigate, checkoutId, encodedEmail]);

  if (!checkoutId) {
    return null;
  }

  return (
    <Stack justify="space-between">
      <CheckoutOrderNumbers checkoutId={checkoutId} />

      <Space h={96} />
      <ClickableOptionsCard>
        <ClickableOptionsCard.Row title="View Purchase History" onClick={handlePurchase} />

        {isTicket && (
          <ClickableOptionsCard.Row title="View Ticket(s)" onClick={handleTicketPurchase} />
        )}

        {isMembership && (
          <ClickableOptionsCard.Row title="View Membership Card" onClick={handleMembership} />
        )}
      </ClickableOptionsCard>
    </Stack>
  );
};

const CheckoutConfirmationModal = ({
  opened,
  onClose,
  checkoutId,
}: CheckoutConfirmationModalProps) => {
  if (!checkoutId) {
    return null;
  }
  return (
    <Modal opened={opened} onClose={onClose} title="Thank you for your order!">
      {checkoutId ? <CheckoutConfirmationModalContent checkoutId={checkoutId} /> : null}
    </Modal>
  );
};

export { CheckoutConfirmationModal };
